import { Action } from '@ngrx/store';
import { SelectorIdentifier } from './selector-identifier';
import { ValidationRule } from './validator-models';
import { SelectableOption, SelectableValue } from './selectables';

/**
 * Enum defining all available field types
 */
export enum FieldType {
    Text = 'text',
    Number = 'number',
    Select = 'select',
    Toggle = 'toggle',
    Date = 'date',
    Autocomplete = 'autocomplete',
    Checkbox = 'checkbox',
    Range = 'range',
    UI = 'ui',
    Section = 'section',
}

/**
 * Base interface for all form fields with common properties
 */
export interface BaseFieldItem<FieldValue = unknown, EventValue = FieldValue> {
    id: string;
    name: string;
    value?: FieldValue;
    label?: string;
    placeholder?: string;
    placeholderSuffix?: string;
    validators?: ValidationRule[];
    events?: {
        [key: string]: (value: EventValue) => Action[];
    };
    visible?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    errors?: { [key: string]: string };
    panelClass?: string;
    preserveOldValues?: boolean;
    allowEmailCharacters?: boolean;
    allowPhoneCharacters?: boolean;
}

/**
 * Text input field
 */
export interface TextField extends BaseFieldItem<string> {
    type: FieldType.Text;
    mask?: string;
}

/**
 * Number input field
 */
export interface NumberField extends BaseFieldItem<number> {
    type: FieldType.Number;
    mask?: string;
    decimalPlaces?: number;
    formatDecimalPlaces?: boolean;
    inputType?: 'number';
    integerOnly?: boolean;
    min?: number;
    max?: number;
    step?: number;
    blockNonNumeric?: boolean;
}

/**
 * Select dropdown field
 */
export interface SelectField<
    OptionsModel extends SelectableOption = unknown,
    Value extends SelectableValue = string | number | boolean,
> extends BaseFieldItem<Value, OptionsModel> {
    type: FieldType.Select;
    optionsSelector?: SelectorIdentifier | OptionsModel[];
    valueMapper: (option: OptionsModel) => Value;
    displayMapper: (option: OptionsModel) => string;
}

/**
 * Toggle/switch field
 */
export interface ToggleField extends BaseFieldItem<boolean> {
    type: FieldType.Toggle;
}

/**
 * Date/time picker field
 */
export interface DateField extends BaseFieldItem<Date | string> {
    type: FieldType.Date;
    dateFormat?: string;
    datePickerType?: 'date' | 'datetime' | 'time';
    timeInterval?: number;
}

/**
 * Autocomplete field with dropdown suggestions
 */
export interface AutocompleteField<
    OptionsModel extends SelectableOption = unknown,
    Value extends SelectableValue = string,
> extends BaseFieldItem<Value, OptionsModel> {
    type: FieldType.Autocomplete;
    optionsSelector?: SelectorIdentifier;
    valueMapper: (option: OptionsModel) => Value;
    displayMapper: (option: OptionsModel) => string;
    clearable?: boolean;
    autoClearOnFocus?: boolean;
}

/**
 * Checkbox field
 */
export interface CheckboxField extends BaseFieldItem<boolean> {
    type: FieldType.Checkbox;
}

/**
 * Range slider field
 */
export interface RangeField extends BaseFieldItem<number> {
    type: FieldType.Range;
    min?: number;
    max?: number;
    step?: number;
    displayFormatter?: (value: number) => string;
    rangeTextMap?: { [key: number]: string };
    thumbLabel?: boolean;
    vertical?: boolean;
    tickInterval?: number | 'auto';
    displayWith?: (value: number) => string;
}

/**
 * Union type for all field types
 */
export type FieldItem =
    | TextField
    | NumberField
    | SelectField
    | ToggleField
    | DateField
    | AutocompleteField
    | CheckboxField
    | RangeField;
